import { ReactElement } from 'react';
import { RouterProvider } from 'react-aria';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import ErrorPage from '../../ErrorPage';
import Admin from '../admin';
import { CohortsAdmin } from '../admin/pages/cohorts/CohortsAdmin';
import CommunityAnnouncementsAdmin from '../admin/pages/community-announcements/CommunityAnnouncementsAdmin';
import CommunityWinsAdmin from '../admin/pages/community-wins/CommunityWinsAdmin';
import EventsAdmin from '../admin/pages/events/EventsAdmin';
import FundingOpportunitiesAdmin from '../admin/pages/funding-opportunities/FundingOpportunitiesAdmin';
import { AdminHome } from '../admin/pages/home/AdminHome';
import { MatchingAdmin } from '../admin/pages/introductions/IntroductionsAdmin';
import JobPostingsAdmin from '../admin/pages/JobPostings/JobPostingsAdmin';
import { CommunityMeetingDetail } from '../admin/pages/meetings/CommunityMeetingDetail';
import { EIRMeetingDetail } from '../admin/pages/meetings/EIRMeetingDetail';
import { MeetingsAdmin } from '../admin/pages/meetings/MeetingsAdmin';
import { OrganizationsAdmin } from '../admin/pages/organizations/OrganizationsAdmin';
import { PartnersAdmin } from '../admin/pages/partners/PartnersAdmin';
import { ProfessionalsAdmin } from '../admin/pages/professionals/ProfessionalsAdmin';
import { ProjectsAdmin } from '../admin/pages/projects/ProjectsAdmin';
import { RepresentativesAdmin } from '../admin/pages/representatives/RepresentativesAdmin';
import { ProUserSearchPage } from '../admin/pages/search/ProUserSearchPage';
import { RepUserSearchPage } from '../admin/pages/search/RepUserSearchPage';
import App from '../app';
import { Application } from '../application/Application';
import { ApplicationStartPage } from '../auth/account-verification/ApplicationStartPage';
import { EmailVerificationPage } from '../auth/account-verification/EmailVerificationPage';
import { OneTimeLoginPage } from '../auth/account-verification/OneTimeLoginPage';
import { PasswordResetEmailVerificationPage } from '../auth/account-verification/PasswordResetEmailVerificationPage';
import { ChangePasswordPage } from '../auth/forgot-password/ChangePasswordPage';
import { ForgotPasswordPage } from '../auth/forgot-password/ForgotPasswordPage';
import { UpdatePasswordPage } from '../auth/forgot-password/UpdatePasswordPage';
import { HomeSignIn } from '../auth/sign-in/HomeSignIn';
import { CorporateProSignupPage } from '../auth/sign-up/corporate-pro-sign-up/CorporateProSignupPage';
import { HomeSignUp } from '../auth/sign-up/HomeSignUp';
import { IndividualProSignUpPage } from '../auth/sign-up/individual-pro-sign-up/IndividualProSignUpPage';
import BusinessChallengesForm from '../dashboard/business-challenges/org-business-challenges-form';
import { RepSignUpPage } from '../auth/sign-up/rep-sign-up/RepSignUpPage';
import { VolunteerSelectionPage } from '../auth/sign-up/volunteer-selection/VolunteerSelectionPage';
import Dashboard from '../dashboard';
import AccountPanel from '../dashboard/account';
import { InviteOrgColleaguePage } from '../dashboard/account/InviteOrgColleaguePage';
import { CommunityHomePage } from '../dashboard/community/community-home/CommunityHomePage';
import { CommunityWinsPage } from '../dashboard/community/community-wins/CommunityWinsPage';
import { EventsPage } from '../dashboard/community/events/EventsPage';
import { FundingOpportunitiesPage } from '../dashboard/community/funding-opportunities/FundingOpportunitiesPage';
import { JobPostingsPage } from '../dashboard/community/job_openings/JobOpeningsPage';
import DashboardHomePanel from '../dashboard/home/DashboardHomePanel';
import MyProjectsPage from '../dashboard/my-projects';
import UpdateYourEmail from '../dashboard/UpdateYourEmail';
import { OrganizationProfile } from '../directory/organizations';
import ProfessionalProfile from '../directory/professionals';
import ProjectProfile from '../directory/projects';
import Healthcheck from '../Healthcheck';
import HomePage from '../home';
import ProfessionalsPage from '../landing/ProfessionalsPage';
import SocialEnterprisesPage from '../landing/SocialEnterprisesPage';
import { EditProfileRoot } from '../profile/EditProfileRoot';
import ProjectSearch from '../search/projects/ProjectSearch';
import UserSearch from '../search/users/UserSearch';
import { ADMIN_ROUTES, ROUTES } from './consts';
import ContentfulTarget from '../contentful/ContentfulTarget';
import IndividualProjectTrackerPage from '../project-hub/individual';
import TeamProjectTrackerPage from '../project-hub/team';
import PartnerLandingPageV2 from '../impact-partner/PartnerLandingPageV2';
import TeamsAdmin from '../admin/pages/teams/TeamsAdmin';
import { ContentfulRootData } from '../contentful/util/types';
import CreateProjectPage from '../projects/CreateProjectPage';
import EditProjectPage from '../projects/EditProjectPage';
import { TeamDetailPage } from '../admin/pages/teams/TeamDetailPage';
import { CohortTeams } from '../admin/pages/teams/CohortTeams';
import { OrganizationDetail } from '../admin/pages/organizations/OrganizationDetail';
import { ProjectDetail } from '../admin/pages/projects/ProjectDetail';
import { ProfessionalDetail } from '../admin/pages/professionals/ProfessionalDetail';
import { IntroductionDetail } from '../admin/pages/introductions/IntroductionDetail';
import { SapCohortSelectionPage } from '../application/program/sap/SAPCohortSelection';
import { IntroductionEdit } from '../admin/pages/introductions/IntroductionEdit';
import { OrganizationEdit } from '../admin/pages/organizations/OrganizationEdit';
import { ApplicationClosedPage } from '../application/program/shared/ApplicationsClosed';
import { ProjectPlanPage } from '../project-plan/ProjectPlanPage';
import { CorporateDashboard, CorporateHomePage } from '../corporate';
import { TeamsMatchAdmin } from '../admin/pages/teams-matching/TeamsMatchAdmin';
import { TeamMatchDetail } from '../admin/pages/teams-matching/TeamMatchDetailPage';

// Conditionally load Contentful data for SSR rendering of routes

let contentfulRootData: ContentfulRootData | null = null;
if (
  (import.meta.env.VITE_RENDER_CONTENTFUL && import.meta.env.SSR) ||
  import.meta.env.VITE_ENVIRONMENT === 'development'
) {
  console.log('Loading Contentful data for SSR rendering of routes...');
  // Here we use import.meta.glob to conditionally load the Contentful data JSON file. This file will only
  // exist in contexts where we're actually building the Contentful pages, but Rollup (via Vite) will statically analyze even
  // async imports and error if you use normal import syntax. Using meta glob steps around this.
  const contentfulDataPath = '../../../../../tmp/contentful_pages_resolved.json';
  // Unfortunately you are required to use a literal string with import.meta.glob, hence duplicating the path
  // above. This arg should be 1-1 with contentfulDataPath
  const contentfulFiles = import.meta.glob('../../../../../tmp/contentful_pages_resolved.json', {
    import: 'default',
  });
  if (contentfulFiles[contentfulDataPath]) {
    contentfulRootData = (await contentfulFiles[contentfulDataPath]()) as ContentfulRootData;
  }
}

// TODO: Configure with our custom routing options
// Note that this is RAC; may need similar for bare RA?
declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: {
      optionOne: boolean;
      optionTwo: string;
    };
  }
}

const ReactAriaRouter = ({ children }: { children: ReactElement }) => {
  const [, setLocation] = useLocation();

  return (
    <RouterProvider
      // TODO: These navigate & useHref functions don't do anything that isn't default wouter behavior
      // yet. They were used to debug intra-app navigation and may need to accept options for supporting
      // analytics reporting
      navigate={(to) => {
        console.log(`Navigating to: ${to}`);
        if (!to.startsWith('/')) {
          window.location.href = to;
        } else {
          setLocation(to, {});
        }
      }}
      useHref={(href) => {
        return href;
      }}
    >
      {children}
    </RouterProvider>
  );
};

export const routerConfig = (
  <Route path={ROUTES.HOME} nest>
    <ReactAriaRouter>
      {/* react-error-boundary is only supported in client contexts */}
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Switch>
          {/* TODO: I moved <App> down to be a child of <Switch> so that its meta tags would be re-applied on
           * ever route change (unless overridden by children). Is this dangerous? */}
          <App>
            <Route path={ROUTES.HOME}>
              <HomePage contentfulData={contentfulRootData} />
            </Route>
            <Route path='/platform' nest>
              {/* Unauthed pages */}
              <Route path={ROUTES.SOCIAL_ENTERPRISES}>
                <SocialEnterprisesPage />
              </Route>
              <Route path={ROUTES.PROFESSIONALS}>
                <ProfessionalsPage />
              </Route>
              <Route path={ROUTES.IMPACT_PARTNERS}>
                <PartnerLandingPageV2 />
              </Route>
              <Route path={ROUTES.PROJECT_PLAN}>
                <ProjectPlanPage />
              </Route>
              <Route path={ROUTES.AUTH} nest>
                <Switch>
                  <Route path={ROUTES.SIGN_UP} nest>
                    <Route path={ROUTES.SIGN_UP_PRO}>
                      <VolunteerSelectionPage />
                    </Route>
                    <Route path={ROUTES.SIGN_UP_REP}>
                      <RepSignUpPage />
                    </Route>
                    <Route path={ROUTES.SIGN_UP_PRO_INDIVIDUAL}>
                      <IndividualProSignUpPage />
                    </Route>
                    <Route path={ROUTES.SIGN_UP_PRO_CORPORATE}>
                      <CorporateProSignupPage />
                    </Route>
                    <Route path='/'>
                      <HomeSignUp />
                    </Route>
                  </Route>
                  <Route path={ROUTES.EMAIL_VERIFICATION}>
                    <EmailVerificationPage />
                  </Route>
                  <Route path={ROUTES.LOGIN}>
                    <HomeSignIn />
                  </Route>
                  <Route path={ROUTES.WELCOME_LOGIN}>
                    <OneTimeLoginPage />
                  </Route>
                  <Route path={ROUTES.FORGOT_PASSWORD}>
                    <ForgotPasswordPage />
                  </Route>
                  <Route path={ROUTES.UPDATE_PASSWORD}>
                    <UpdatePasswordPage />
                  </Route>
                  <Route path={ROUTES.APPLICATION_START}>
                    <ApplicationStartPage />
                  </Route>
                  <Route path={ROUTES.PASSWORD_RESET_EMAIL_VERIFICATION}>
                    <PasswordResetEmailVerificationPage />
                  </Route>
                </Switch>
              </Route>
              {/* Authed pages; visiting when unauthed will redirect to sign-in */}
              <Route path={ROUTES.APPLICATION_CLOSED}>
                <ApplicationClosedPage />
              </Route>
              <Route path={ROUTES.COHORT_SELECTION}>
                <SapCohortSelectionPage />
              </Route>
              <Route path={ROUTES.SEARCH_PROJECTS}>
                <ProjectSearch />
              </Route>
              <Route path={ROUTES.USER_SEARCH}>
                <UserSearch />
              </Route>
              <Route path={ROUTES.INVITE_ORG_COLLEAGUE}>
                <InviteOrgColleaguePage />
              </Route>
              <Route path={ROUTES.BUSINESS_CHALLENGES} nest>
                <BusinessChallengesForm />
              </Route>
              {/* Redirect to the first step of the project creation flow; this prevents us from having to check
               for a null stepId */}
              <Route path={ROUTES.CREATE_PROJECT}>
                <Redirect to={ROUTES.CREATE_PROJECT + '/1'} />
              </Route>
              {/* Create project flow, where stepId is one of four steps in the process */}
              <Route path={ROUTES.CREATE_PROJECT + '/:stepId'} component={CreateProjectPage} />
              <Route path={ROUTES.DASHBOARD} nest>
                <Dashboard>
                  <Switch>
                    <Route path={ROUTES.MY_PROJECTS}>
                      <MyProjectsPage />
                    </Route>
                    <Route
                      path={ROUTES.EDIT_PROJECT}
                      component={() => <EditProjectPage mode='rep-edit' />}
                    />
                    <Route path={ROUTES.ACCOUNT}>
                      <AccountPanel />
                    </Route>
                    <Route path={ROUTES.COMMUNITY} nest>
                      <Route path={ROUTES.JOB_OPENINGS}>
                        <JobPostingsPage />
                      </Route>
                      <Route path={ROUTES.EVENTS}>
                        <EventsPage />
                      </Route>
                      <Route path={ROUTES.FUNDING_OPPORTUNITIES}>
                        <FundingOpportunitiesPage />
                      </Route>
                      <Route path={ROUTES.COMMUNITY_WINS}>
                        <CommunityWinsPage />
                      </Route>
                      <Route path='/'>
                        <CommunityHomePage />
                      </Route>
                    </Route>
                    <Route path='/'>
                      <DashboardHomePanel />
                    </Route>
                  </Switch>
                </Dashboard>
              </Route>
              <Route path={ROUTES.APPLICATION} nest>
                <Application />
              </Route>
              <Route path={ROUTES.EDIT_PROFILE} nest>
                <EditProfileRoot />
              </Route>
              <Route path={ROUTES.UPDATE_YOUR_EMAIL}>
                <UpdateYourEmail />
              </Route>
              <Route path={ROUTES.PROFILE} nest>
                <Route path={ROUTES.CHANGE_PASSWORD}>
                  <ChangePasswordPage />
                </Route>
              </Route>
              <Route path={ROUTES.DIRECTORY} nest>
                <Route path={ROUTES.DIRECTORY_PROFESSIONALS}>
                  <ProfessionalProfile />
                </Route>
                <Route path={ROUTES.DIRECTORY_ORGANIZATIONS}>
                  <OrganizationProfile />
                </Route>
                <Route path={ROUTES.DIRECTORY_PROJECTS}>
                  <ProjectProfile />
                </Route>
              </Route>
              <Route path={ROUTES.PROJECT_HUB}>
                <IndividualProjectTrackerPage />
              </Route>
              <Route path={ROUTES.PROJECT_HUB_TEAM}>
                <TeamProjectTrackerPage />
              </Route>
              <Route path={ROUTES.CORPORATE_HOME}>
                <CorporateHomePage />
              </Route>
              <Route path={ROUTES.CORPORATE_DASHBOARD}>
                <CorporateDashboard />
              </Route>
              <Route path={ROUTES.ADMIN} nest>
                <Admin>
                  <Route path={ADMIN_ROUTES.JOB_POSTINGS}>
                    <JobPostingsAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.COMMUNITY_WINS}>
                    <CommunityWinsAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.COMMUNITY_ANNOUNCEMENTS}>
                    <CommunityAnnouncementsAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.EVENTS}>
                    <EventsAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.FUNDING_OPPORTUNITIES}>
                    <FundingOpportunitiesAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.PROJECTS} nest>
                    <Route path={ADMIN_ROUTES.PROJECT_DETAIL}>
                      <ProjectDetail />
                    </Route>
                    <Route path={ADMIN_ROUTES.PROJECT_EDIT}>
                      <EditProjectPage mode='admin' />
                    </Route>
                    <Route path='/'>
                      <ProjectsAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.MATCHING} nest>
                    <Route path={ADMIN_ROUTES.INTRODUCTION_EDIT}>
                      <IntroductionEdit />
                    </Route>
                    <Route path={ADMIN_ROUTES.INTRODUCTION_DETAIL}>
                      <IntroductionDetail />
                    </Route>
                    <Route path='/'>
                      <MatchingAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.MATCHING_TEAMS} nest>
                    <Route path={ADMIN_ROUTES.TEAM_MATCH_DETAIL}>
                      <TeamMatchDetail />
                    </Route>
                    <Route path={'/'}>
                      <TeamsMatchAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.ORGANIZATIONS} nest>
                    <Route path={ADMIN_ROUTES.ORGANIZATION_DETAIL}>
                      <OrganizationDetail />
                    </Route>
                    <Route path={ADMIN_ROUTES.ORGANIZATION_EDIT}>
                      <OrganizationEdit />
                    </Route>
                    <Route path='/'>
                      <OrganizationsAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.PROFESSIONALS} nest>
                    <Route path={ADMIN_ROUTES.PROFESSIONAL_DETAIL}>
                      <ProfessionalDetail />
                    </Route>
                    <Route path='/'>
                      <ProfessionalsAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.MEETINGS} nest>
                    <Route path={ADMIN_ROUTES.COMMUNITY_MEETING_DETAIL}>
                      <CommunityMeetingDetail />
                    </Route>
                    <Route path={ADMIN_ROUTES.EIR_MEETING_DETAIL}>
                      <EIRMeetingDetail />
                    </Route>
                    <Route path='/'>
                      <MeetingsAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.SEARCH_PROFESSIONALS}>
                    <ProUserSearchPage />
                  </Route>
                  <Route path={ADMIN_ROUTES.SEARCH_REPRESENTATIVES}>
                    <RepUserSearchPage />
                  </Route>
                  <Route path={ADMIN_ROUTES.REPRESENTATIVES}>
                    <RepresentativesAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.PARTNERS}>
                    <PartnersAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.COHORTS}>
                    <CohortsAdmin />
                  </Route>
                  <Route path={ADMIN_ROUTES.TEAMS} nest>
                    <Route path={ADMIN_ROUTES.TEAM_DETAIL}>
                      <TeamDetailPage />
                    </Route>
                    <Route path={ADMIN_ROUTES.COHORT_TEAMS}>
                      <CohortTeams />
                    </Route>
                    <Route path='/'>
                      <TeamsAdmin />
                    </Route>
                  </Route>
                  <Route path={ADMIN_ROUTES.HOME}>
                    <AdminHome />
                  </Route>
                </Admin>
              </Route>
              <Route path={ROUTES.HEALTHCHECK} component={Healthcheck} />
              {/* Allows redirecting into the error page in addition to the default pass through below */}
              <Route path={ROUTES.ERROR} component={ErrorPage} />
            </Route>
            {/* Catch-all route to handle Contentful pages on routes that don't start with /platform. We fallback to showing Contentful,
             * which itself renders a generic error page if no Contentful page with the matching slug exists. */}
            <Route path={/^(?!\/platform).*/}>
              <ContentfulTarget contentfulData={contentfulRootData} />
            </Route>
          </App>
        </Switch>
      </ErrorBoundary>
    </ReactAriaRouter>
    {/* Show the homepage by default. Used in cases where setting the href to '/' is insufficient as our components do SPA route-relative routing.
     * Note that attempting to render this via '/' under the '/platform' route does not match */}
    <Route path='/platform'>
      <Redirect to={ROUTES.HOME} />
    </Route>
  </Route>
);
